/**
 * Phase 4A.7 — crew.ai-style manager/delegation, pure logic.
 * No LLM / no supabase here (crewPure.test.ts). The runtime loop lives in
 * crewExecutor.ts and injects the manager + worker call functions so this stays
 * deterministic and testable.
 */

export interface CrewWorker {
  /** stable id used in delegate decisions (agent id or node id) */
  id: string;
  name: string;
  description?: string;
}

export type CrewDecision =
  | { action: "delegate"; worker: string; task: string }
  | { action: "finish"; answer: string };

export const CREW_MAX_ROUNDS_CAP = 12;

/** Clamp requested manager rounds; default scales with the roster size. */
export function clampCrewRounds(requested: number | undefined, workerCount: number): number {
  const def = Math.max(2, Math.min(workerCount * 2, CREW_MAX_ROUNDS_CAP));
  const n = requested && requested > 0 ? Math.floor(requested) : def;
  return Math.max(1, Math.min(n, CREW_MAX_ROUNDS_CAP));
}

/** Numbered roster block for the manager system prompt. */
export function formatWorkerRoster(workers: CrewWorker[]): string {
  if (!workers.length) return "(no workers connected)";
  return workers
    .map((w, i) => `${i + 1}. id="${w.id}" name="${w.name}"${w.description ? ` — ${w.description}` : ""}`)
    .join("\n");
}

/** Transcript of completed delegations, fed back to the manager each round. */
export function formatBlackboard(entries: Array<{ worker: string; task: string; result: string }>): string {
  if (!entries.length) return "(nothing completed yet)";
  return entries
    .map((e, i) => `### Step ${i + 1} — ${e.worker}\nTask: ${e.task}\nResult:\n${e.result}`)
    .join("\n\n");
}

/**
 * Parse the manager's raw text into a decision. Tolerates code fences and
 * surrounding prose. If no valid decision is found, treat the whole text as a
 * finish answer — the crew always terminates rather than hanging.
 */
export function parseManagerDecision(raw: string, validWorkerIds: string[]): CrewDecision {
  const jsonStr = extractJson(raw);
  if (jsonStr) {
    try {
      const obj = JSON.parse(jsonStr) as Record<string, unknown>;
      const action = String(obj.action ?? "").toLowerCase();
      if (action === "delegate") {
        const worker = String(obj.worker ?? obj.workerId ?? "").trim();
        const task = String(obj.task ?? obj.instruction ?? "").trim();
        if (worker && task && validWorkerIds.includes(worker)) {
          return { action: "delegate", worker, task };
        }
        // delegate to an unknown/invalid worker → fall through to finish
      }
      if (action === "finish") {
        return { action: "finish", answer: String(obj.answer ?? obj.result ?? raw).trim() };
      }
    } catch {
      // fall through
    }
  }
  return { action: "finish", answer: raw.trim() };
}

function extractJson(text: string): string | null {
  // fenced ```json ... ``` first
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const body = fence ? fence[1] : text;
  const start = body.indexOf("{");
  const end = body.lastIndexOf("}");
  if (start >= 0 && end > start) return body.slice(start, end + 1);
  return null;
}

/** System prompt that forces the manager into the delegate/finish JSON protocol. */
export function buildManagerSystemPrompt(
  goal: string,
  workers: CrewWorker[],
  blackboard: Array<{ worker: string; task: string; result: string }>,
  roundsLeft: number,
  extraInstructions?: string,
): string {
  return [
    "You are the MANAGER of a team of AI worker agents. Decompose the goal and delegate ONE task at a time to the best-suited worker, using their completed results to decide the next step. When the goal is met (or no further useful delegation remains), finish with a synthesized answer.",
    extraInstructions ? `\nManager guidance:\n${extraInstructions}` : "",
    `\nGOAL:\n${goal}`,
    `\nWORKERS:\n${formatWorkerRoster(workers)}`,
    `\nCOMPLETED SO FAR:\n${formatBlackboard(blackboard)}`,
    `\nRounds remaining: ${roundsLeft}. Respond with ONE JSON object and nothing else:`,
    `  {"action":"delegate","worker":"<worker id>","task":"<specific instruction>"}`,
    `  or {"action":"finish","answer":"<final synthesized answer>"}`,
  ]
    .filter(Boolean)
    .join("\n");
}
